<template>
	<div />
</template>

<script setup lang="ts">
	import { onMounted, ref } from 'vue'
	import ProfileWizard from '~/components/profile/profileWizard/ProfileWizard.vue'
	import ProfileWizardSimple from '~/components/profile/profileWizard/ProfileWizardSimple.vue'
	import {
		DialogBuilder,
		DialogController,
	} from '~/components/passporterUi/Dialog/dialogController'
	import EventsService from '~/services/events'
	import { NewFeatureActions, NewFeatureDialogProps } from '~/actions/newFeature/action'
	import { OnBoardingDialogProps } from '~/composables/onBoarding'
	import NewFeatureDialog from '~/components/shared/NewFeatureDialog.vue'
	import OnBoardingDialog from '~/components/shared/OnBoardingDialog.vue'
	import { Profile } from '~/models/Profile'

	interface Props {
		profile?: Profile
	}
	const props = defineProps<Props>()

	const route = useRoute()

	const dialogQueue = ref<Array<() => void>>([])
	const isDialogOpen = ref<boolean>()

	onMounted(() => {
		EventsService.listenOpenNewFeature(openOrQueueNewFeature)
		EventsService.listenOpenOnBoarding(openOrQueueOnBoarding)
		EventsService.listenOpenProfileWizard(openProfileWizard)
		if (props.profile && !props.profile?.birthYear)
			NewFeatureActions.openProfileWizard(route.name as string)
	})

	const openProfileWizard = (simple?: boolean) => {
		isDialogOpen.value = true
		const profileWizard = createWizard(simple)
		profileWizard.open()
		profileWizard.onClose(() => {
			onCloseDialog()
			EventsService.emitProfileWizardCompleted()
		})
	}

	const openOrQueueNewFeature = (newFeature: NewFeatureDialogProps) => {
		if (!isDialogOpen.value) return openNewFeature(newFeature)
		dialogQueue.value.push(() => openNewFeature(newFeature))
	}

	const openOrQueueOnBoarding = (onBoarding: OnBoardingDialogProps) => {
		if (!isDialogOpen.value) return openOnBoarding(onBoarding)
		dialogQueue.value.push(() => openOnBoarding(onBoarding))
	}

	const openOnBoarding = (onBoarding: OnBoardingDialogProps) => {
		const dialog = new DialogController(
			new DialogBuilder(OnBoardingDialog)
				.size('s')
				.props(onBoarding)
				.mobileType('bottomSheet')
				.bottomSheetType('expanded')
				.borderless()
				.build()
		)
		isDialogOpen.value = true
		dialog.onClose(onCloseDialog)
		dialog.onAccept(onCloseDialog)
		setTimeout(() => dialog.open(), 100)
	}

	const openNewFeature = (newFeature: NewFeatureDialogProps) => {
		const dialog = createNewFeature(newFeature)
		isDialogOpen.value = true
		dialog.onClose(onCloseDialog)
		dialog.onAccept(onCloseDialog)
		setTimeout(() => dialog.open(), 100)
	}

	const onCloseDialog = () => {
		isDialogOpen.value = false
		const callback = dialogQueue.value.shift()
		if (callback) callback()
	}

	const createNewFeature = (props: NewFeatureDialogProps) => {
		const newFeatureDialog = new DialogBuilder(NewFeatureDialog)
			.size('s')
			.props(props)
			.mobileType('bottomSheet')
			.bottomSheetType('expanded')
			.borderless()
			.build()
		return new DialogController(newFeatureDialog)
	}

	function createWizard(simple: boolean = false) {
		let component = ProfileWizard
		if (simple) component = ProfileWizardSimple
		const setup = new DialogBuilder(component)
			.borderless()
			.size('s')
			.closeOnBackdropClick(false)
			.mobileType('fullScreen')
			.closeButton(false)
			.build()
		return new DialogController(setup)
	}
</script>
